import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { useEffect, useMemo, useRef } from 'react';
import ReactECharts from 'echarts-for-react';
import { graphic } from 'echarts';
import { COLORS, MEASURE, SERIES, FONT_BODY, withAlpha } from './palette';
import { currencyFormatter } from 'src/utils/translator';

// ---------------------------------------------------------------------------
// Shared base styling applied to every chart (ported from custom-analytics,
// recolored to Livable's light theme).
// ---------------------------------------------------------------------------
const axisLabel = {
  color: COLORS.textMuted,
  fontFamily: FONT_BODY,
  fontSize: 11
};
const axisLine = {
  lineStyle: {
    color: COLORS.border
  }
};
const splitLine = {
  lineStyle: {
    color: COLORS.border,
    type: 'dashed'
  }
};
const tooltip = {
  trigger: 'axis',
  backgroundColor: 'rgba(255,255,255,0.97)',
  borderColor: COLORS.borderStrong,
  borderWidth: 1,
  padding: [10, 14],
  textStyle: {
    color: COLORS.text,
    fontFamily: FONT_BODY,
    fontSize: 12
  },
  extraCssText: 'backdrop-filter: blur(8px); border-radius: 6px; box-shadow: 0 10px 30px rgba(33,41,52,0.14);',
  valueFormatter: value => typeof value === 'number' ? currencyFormatter(value) : value
};
const legend = data => ({
  data,
  bottom: 0,
  icon: 'roundRect',
  itemWidth: 11,
  itemHeight: 11,
  itemGap: 18,
  textStyle: {
    color: COLORS.textMuted,
    fontFamily: FONT_BODY,
    fontSize: 11
  }
});
const baseGrid = {
  top: 24,
  right: 18,
  bottom: 48,
  left: 48,
  containLabel: true
};

// Vertical gradient fill for a given hex color.
const vGradient = function (hex) {
  let topAlpha = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 0.95;
  let botAlpha = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 0.55;
  return new graphic.LinearGradient(0, 0, 0, 1, [{
    offset: 0,
    color: withAlpha(hex, topAlpha)
  }, {
    offset: 1,
    color: withAlpha(hex, botAlpha)
  }]);
};
const ECHARTS_STYLE = {
  height: '100%',
  width: '100%'
};
const OPTS = {
  renderer: 'canvas'
};

// ECharts only auto-resizes on the WINDOW resize event, so any container reflow
// that doesn't fire one — sidebar collapse, panel wrap, a span change, HMR —
// leaves the canvas drawn at a stale size. A ResizeObserver on the chart's own
// box re-fits it on ANY size change, debounced to a frame so rapid drags don't
// thrash. This is why charts never look stretched/clipped after a resize.
const Chart = _ref => {
  let option = _ref.option,
    _ref$height = _ref.height,
    height = _ref$height === void 0 ? 300 : _ref$height;
  const ref = useRef(null);
  const boxRef = useRef(null);
  useEffect(() => {
    const fire = () => {
      var _ref$current, _ref$current$getEchar;
      return (_ref$current = ref.current) === null || _ref$current === void 0 || (_ref$current$getEchar = _ref$current.getEchartsInstance) === null || _ref$current$getEchar === void 0 || (_ref$current$getEchar = _ref$current$getEchar.call(_ref$current)) === null || _ref$current$getEchar === void 0 ? void 0 : _ref$current$getEchar.resize();
    };
    const raf = requestAnimationFrame(fire);
    const t = setTimeout(fire, 90);
    let roRaf = 0;
    const ro = new ResizeObserver(() => {
      cancelAnimationFrame(roRaf);
      roRaf = requestAnimationFrame(fire);
    });
    if (boxRef.current) ro.observe(boxRef.current);
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(t);
      cancelAnimationFrame(roRaf);
      ro.disconnect();
    };
  }, []);
  const animated = useMemo(() => _objectSpread({
    animationDuration: 760,
    animationEasing: 'cubicOut',
    animationDelay: idx => idx * 22
  }, option), [option]);
  return /*#__PURE__*/React.createElement("div", {
    ref: boxRef,
    style: {
      height
    }
  }, /*#__PURE__*/React.createElement(ReactECharts, {
    ref: ref,
    option: animated,
    style: ECHARTS_STYLE,
    opts: OPTS,
    notMerge: true
  }));
};

// ---------------------------------------------------------------------------
// Paid / Unpaid / Credited grouped bar — used for the "by property", "by unit"
// and "by period" breakdowns. `data` is an array of report items carrying
// { label, paid, unpaid, credited }.
// ---------------------------------------------------------------------------
export const PaidUnpaidBar = _ref2 => {
  let data = _ref2.data,
    _ref2$height = _ref2.height,
    height = _ref2$height === void 0 ? 320 : _ref2$height;
  const option = useMemo(() => {
    const categories = data.map(d => d.label);
    const measures = [{
      name: 'Recovered',
      key: 'paid',
      color: MEASURE.paid
    }, {
      name: 'Unpaid',
      key: 'unpaid',
      color: MEASURE.unpaid
    }, {
      name: 'Credited',
      key: 'credited',
      color: MEASURE.credited
    }];
    return {
      tooltip,
      legend: legend(measures.map(m => m.name)),
      grid: baseGrid,
      xAxis: {
        type: 'category',
        data: categories,
        axisLabel: _objectSpread(_objectSpread({}, axisLabel), {}, {
          interval: 0,
          hideOverlap: true,
          rotate: categories.length > 6 ? 30 : 0
        }),
        axisLine,
        axisTick: {
          show: false
        }
      },
      yAxis: {
        type: 'value',
        axisLabel: _objectSpread(_objectSpread({}, axisLabel), {}, {
          formatter: value => currencyFormatter(value)
        }),
        splitLine,
        axisLine: {
          show: false
        }
      },
      series: measures.map(m => ({
        name: m.name,
        type: 'bar',
        data: data.map(d => Number(d[m.key]) || 0),
        barMaxWidth: 28,
        itemStyle: {
          color: vGradient(m.color),
          borderRadius: [3, 3, 0, 0]
        },
        emphasis: {
          focus: 'series'
        }
      }))
    };
  }, [data]);
  return /*#__PURE__*/React.createElement(Chart, {
    option: option,
    height: height
  });
};

// ---------------------------------------------------------------------------
// Charge-type donut — `data` is an array of { name, value }.
// ---------------------------------------------------------------------------
export const ChargeDonut = _ref3 => {
  let data = _ref3.data,
    _ref3$height = _ref3.height,
    height = _ref3$height === void 0 ? 300 : _ref3$height,
    centerLabel = _ref3.centerLabel;
  const option = useMemo(() => ({
    color: SERIES,
    tooltip: {
      trigger: 'item',
      backgroundColor: 'rgba(255,255,255,0.97)',
      borderColor: COLORS.borderStrong,
      borderWidth: 1,
      padding: [10, 14],
      textStyle: {
        color: COLORS.text,
        fontFamily: FONT_BODY,
        fontSize: 12
      },
      extraCssText: 'border-radius: 6px; box-shadow: 0 10px 30px rgba(33,41,52,0.14);',
      formatter: p => "".concat(p.name, "<br/><b>").concat(currencyFormatter(p.value), "</b> (").concat(p.percent, "%)")
    },
    legend: {
      type: 'scroll',
      orient: 'horizontal',
      bottom: 0,
      icon: 'roundRect',
      itemWidth: 11,
      itemHeight: 11,
      textStyle: {
        color: COLORS.textMuted,
        fontFamily: FONT_BODY,
        fontSize: 11
      }
    },
    series: [{
      type: 'pie',
      radius: ['52%', '74%'],
      center: ['50%', '46%'],
      avoidLabelOverlap: true,
      padAngle: 2,
      itemStyle: {
        borderRadius: 4,
        borderColor: COLORS.panel,
        borderWidth: 2
      },
      label: centerLabel ? {
        show: true,
        position: 'center',
        formatter: centerLabel,
        color: COLORS.text,
        fontFamily: FONT_BODY,
        fontSize: 13,
        fontWeight: 600
      } : {
        show: false
      },
      labelLine: {
        show: false
      },
      emphasis: {
        scaleSize: 6
      },
      data: data.map(d => ({
        name: d.name,
        value: Number(d.value) || 0
      }))
    }]
  }), [data, centerLabel]);
  return /*#__PURE__*/React.createElement(Chart, {
    option: option,
    height: height
  });
};

// ---------------------------------------------------------------------------
// Period trend — smooth area for recovered vs unpaid over time.
// `data` is an array of { label, paid, unpaid } sorted chronologically.
// ---------------------------------------------------------------------------
export const PeriodTrend = _ref4 => {
  let data = _ref4.data,
    _ref4$height = _ref4.height,
    height = _ref4$height === void 0 ? 320 : _ref4$height;
  const option = useMemo(() => {
    const categories = data.map(d => d.label);
    const measures = [{
      name: 'Recovered',
      key: 'paid',
      color: MEASURE.paid
    }, {
      name: 'Unpaid',
      key: 'unpaid',
      color: MEASURE.unpaid
    }];
    return {
      tooltip: _objectSpread(_objectSpread({}, tooltip), {}, {
        axisPointer: {
          type: 'line',
          lineStyle: {
            color: COLORS.borderStrong
          }
        }
      }),
      legend: legend(measures.map(m => m.name)),
      grid: baseGrid,
      xAxis: {
        type: 'category',
        boundaryGap: false,
        data: categories,
        axisLabel,
        axisLine,
        axisTick: {
          show: false
        }
      },
      yAxis: {
        type: 'value',
        axisLabel: _objectSpread(_objectSpread({}, axisLabel), {}, {
          formatter: value => currencyFormatter(value)
        }),
        splitLine,
        axisLine: {
          show: false
        }
      },
      series: measures.map(m => ({
        name: m.name,
        type: 'line',
        smooth: 0.4,
        symbol: 'circle',
        symbolSize: 6,
        showSymbol: false,
        data: data.map(d => Number(d[m.key]) || 0),
        lineStyle: {
          width: 2.5,
          color: m.color,
          shadowColor: withAlpha(m.color, 0.4),
          shadowBlur: 12
        },
        itemStyle: {
          color: m.color,
          borderColor: COLORS.panel,
          borderWidth: 2
        },
        areaStyle: {
          color: new graphic.LinearGradient(0, 0, 0, 1, [{
            offset: 0,
            color: withAlpha(m.color, 0.28)
          }, {
            offset: 1,
            color: withAlpha(m.color, 0.02)
          }])
        },
        emphasis: {
          focus: 'series'
        }
      }))
    };
  }, [data]);
  return /*#__PURE__*/React.createElement(Chart, {
    option: option,
    height: height
  });
};

// ---------------------------------------------------------------------------
// Tiny inline sparkline (SVG, no ECharts) for KPI cards.
// ---------------------------------------------------------------------------
export const Sparkline = _ref5 => {
  let data = _ref5.data,
    color = _ref5.color,
    _ref5$width = _ref5.width,
    width = _ref5$width === void 0 ? 104 : _ref5$width,
    _ref5$height = _ref5.height,
    height = _ref5$height === void 0 ? 30 : _ref5$height;
  if (!data || data.length < 2) return null;
  const min = Math.min(...data);
  const max = Math.max(...data);
  const span = max - min || 1;
  const pts = data.map((v, i) => {
    const x = i / (data.length - 1) * (width - 2) + 1;
    const y = height - 1 - (v - min) / span * (height - 2);
    return "".concat(x.toFixed(1), ",").concat(y.toFixed(1));
  }).join(' ');
  const trendUp = data[data.length - 1] >= data[0];
  const c = color !== null && color !== void 0 ? color : trendUp ? COLORS.green : COLORS.unpaid;
  return /*#__PURE__*/React.createElement("svg", {
    width: width,
    height: height,
    "aria-hidden": true
  }, /*#__PURE__*/React.createElement("polyline", {
    points: pts,
    fill: "none",
    stroke: c,
    strokeWidth: 1.75,
    strokeLinejoin: "round",
    strokeLinecap: "round"
  }));
};